import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterGroup {
  label: string;
  options: FilterOption[];
  key: string;
}

export interface FilterDropdownProps {
  filterOptions?: FilterOption[];
  filterGroups?: FilterGroup[];
  currentFilters?: Record<string, string[]>;
  onApply: (selectedFilters: Record<string, string[]>) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filterOptions = [],
  filterGroups,
  currentFilters,
  onApply,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const open = Boolean(anchorEl);

  // Support legacy filterOptions format
  const groups =
    filterGroups ||
    (filterOptions.length > 0
      ? [
          {
            label: "Filters",
            options: filterOptions,
            key: "default",
          },
        ]
      : []);

  // Sync selectedFilters with currentFilters when dropdown opens
  useEffect(() => {
    if (open && currentFilters) {
      setSelectedFilters(currentFilters);
    }
  }, [open, currentFilters]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    groupKey: string,
  ) => {
    setSelectedFilters((prev) => {
      const groupFilters = prev[groupKey] || [];
      const updatedGroupFilters = event.target.checked
        ? [...groupFilters, event.target.name]
        : groupFilters.filter((name) => name !== event.target.name);

      return {
        ...prev,
        [groupKey]: updatedGroupFilters,
      };
    });
  };

  const handleApply = (clear: boolean) => {
    if (clear) {
      onApply({});
      setSelectedFilters({});
    } else {
      onApply(selectedFilters);
    }
    setAnchorEl(null);
  };

  const totalSelectedCount = Object.values(selectedFilters).reduce(
    (sum, filters) => sum + filters.length,
    0,
  );

  return (
    <Box className="p-0 m-0">
      <Button
        id="filter-dropdown-button"
        sx={{
          "&:hover": {
            borderColor: colors.background.darkGreenHighlight,
            "& #filter-dropdown-count": {
              backgroundColor: colors.background.darkGreenHighlight,
            },
          },
          color: colors.text.primary,
          backgroundColor: colors.background.paper,
          transition: "background-color 0.2s ease",
        }}
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={handleClick}
      >
        Filter
        <Box
          id="filter-dropdown-count"
          className="rounded-full flex items-center justify-center ml-2"
          sx={{
            backgroundColor: colors.primary.main,
            color: colors.text.inverse,
            transition: "background-color 0.2s ease",
            width: "23px",
            height: "23px",
          }}
        >
          {totalSelectedCount}
        </Box>
      </Button>
      <Menu
        id="filter-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "3px",
            p: 0,
            m: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: 500,
            maxHeight: 800,
          }}
        >
          <Typography variant="h5" sx={{ pl: 2, pt: 1 }}>
            Filter
          </Typography>
          <Divider />
          {groups.map((group, groupIndex) => (
            <Box key={group.key}>
              {groupIndex > 0 && <Divider sx={{ my: 1 }} />}
              <Typography
                variant="subtitle2"
                sx={{
                  pl: 2,
                  pt: groupIndex > 0 ? 1 : 0,
                  pb: 0.5,
                  fontWeight: 600,
                  color: colors.text.secondary,
                }}
              >
                {group.label}
              </Typography>
              {group.options.map((option) => (
                <MenuItem key={option.value}>
                  <FormControlLabel
                    sx={{
                      width: "100%",
                    }}
                    control={
                      <Checkbox
                        checked={(selectedFilters[group.key] || []).includes(
                          option.value,
                        )}
                        onChange={(e) => handleChange(e, group.key)}
                        name={option.value}
                      />
                    }
                    label={option.label}
                  />
                </MenuItem>
              ))}
            </Box>
          ))}
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, p: 1 }}>
          <Button
            onClick={() => handleApply(true)}
            variant="outlined"
            color="primary"
            fullWidth
            sx={{
              width: "30%",
              borderRadius: "80px",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={() => handleApply(false)}
            className="rounded-full"
            variant="outlined"
            fullWidth
            disabled={totalSelectedCount === 0}
            sx={{
              borderRadius: "80px",
              width: "30%",
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default FilterDropdown;
